import { useContext, useMemo, useState } from "react";
import { SessionContext } from "../../../common/contexts/SessionContext";
import { SearchInput } from "../../../common/forms/SearchInput/SearchInput";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import TableView, { TableViewLabel } from "../../../common/panels/TableView/TableView";
import { ColumnsSettings } from "../../../common/tables/ColumnsSelector/ColumnsSelector";
import { AlertModal } from "../../../common/modals/AlertModal/AlertModal";
import { MoreDropdown } from "../../../common/buttons/MoreDropdown/MoreDropdown";
import { EntityTable } from "../../../common/tables/EntityTable/EntityTable";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { EntityFilterIndicator } from "../../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { EntityTableTabs } from "../../../common/tables/Tabs/EntityTableTabs";
import { useTabStore } from "../../../common/tables/Tabs/useTabStore";
import { TableTabsDict } from "../../../common/tables/Tabs/TableTabsTypes";
import { DateTimeRenderer } from "../../../common/datetime/DateTimeFormatter";
import { LabNotebookEntry, LabNotebookEntryFilters } from "../../types/LabNotebookEntry";
import { LabNotebookEntriesFilterBar, LabNotebookEntriesFilterForm } from "./LabNotebookEntriesFilterBar";
import { GetPersons } from "../../../common/misc/EntityRenders/EntityRenderer";
import { LinkButton } from "../../../common/buttons/LinkButton/LinkButton";
import { NotSet } from "../../../common/misc/UIconstants";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  getSortStateArray,
  UseEntityTableColumns,
  UseEntityTableDefaultProps,
  UseEntityTableProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
} from "../../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { EntityTableProps } from "../../../common/entity/EntityInterfaces";
import { EntityTableEditButton } from "../../../common/entity/entityComponents/EntityTableEditButton";
import { EntityTableRestoreButton } from "../../../common/entity/entityComponents/EntityTableRestoreButton";
import { EntityTableSoftDeletableButton } from "../../../common/entity/entityComponents/EntityTableSoftDeletableButton";
import { GenericEntityTableComplianceDropdown } from "../../../Entities/GenericComponents/common/GenericEntityTable";
import { NestedDropdown } from "../../../common/buttons/NestedDropdown/NestedDropdown";
import { ExportCsvButton } from "../../../common/tables/ExportCsvButton/ExportCsvButton";
import { ExportPdfButton } from "../../../common/tables/ExportPdfButton/ExportPdfButton";
import { EntityOrderBy, InferSortByOptions } from "../../../api/GenericTypes";

export const switchLabNotebookEntriesDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<LabNotebookEntryFilters>>[] | null | undefined
): SortState<InferSortByOptions<LabNotebookEntryFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;

  return getSortStateArray(sortState, (sort) => {
    switch (sort?.property) {
      case "ENTRY_DATE":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-entry-date",
        };

      case "LAB_NOTEBOOK_NAME":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-lab-notebook",
        };

      case "LAB_NOTEBOOK_EXPERIMENT_NAME":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-lab-notebook-experiment",
        };

      default:
        return { sortDirection: "DESC", headerId: "default-modifiedOn", property: "MODIFIED_ON" };
    }
  });
};

export const defaultLabNotebookEntriesFilters: LabNotebookEntryFilters = {
  sortBy: [
    {
      property: "MODIFIED_ON",
      sortDirection: "DESC",
    },
  ],
};

export const useLabNotebookEntriesTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"notebookEntries">) => {
  const { session } = useContext(SessionContext);

  const defaults: ColumnsSettings<LabNotebookEntry> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
      "default-entry-date": {
        pos: 1,
        active: true,
        header: fieldLabels.entryDate,
        property: "entryDate",
      },
      "default-name": {
        pos: 2,
        active: true,
        header: fieldLabels.name,
        property: "name",
      },
      "default-lab-notebook": {
        pos: 3,
        active: true,
        header: fieldLabels.labNotebook,
        property: "labNotebook",
      },
      "default-lab-notebook-experiment": {
        pos: 4,
        active: true,
        header: fieldLabels.labNotebookExperiment,
        property: "labNotebookExperiment",
      },
      "default-enteredOn": {
        pos: 5,
        active: true,
        header: fieldLabels.enteredOn,
        property: "enteredOn",
      },
      "default-enteredBy": {
        pos: 6,
        active: true,
        header: fieldLabels.enteredBy,
        property: "enteredBy",
      },
      "default-modifiedOn": {
        pos: 7,
        active: true,
        header: fieldLabels.modifiedOn,
        property: "modifiedOn",
      },
      "default-modifiedBy": {
        pos: 8,
        active: true,
        header: fieldLabels.modifiedBy,
        property: "modifiedBy",
      },
    }),
    [fieldLabels]
  );
  const tabStoreDefaults: TableTabsDict<LabNotebookEntry, LabNotebookEntryFilters, LabNotebookEntriesFilterForm> =
    useMemo(
      () => ({
        default: {
          tabId: "default",
          type: "fixed",
          label: "All",
          title: "All",
          icon: "house",
          align: "left",
          xPos: 0,
          settings: {
            columnSettings: {},
            columnWidths: {},
            filters: defaultLabNotebookEntriesFilters,
            sidebarFilters: {},
          },
          forcedSettings: {
            columnSettings: {},
            columnWidths: {},
            filters: {},
            sidebarFilters: {},
          },
        },
        mydata: {
          tabId: "mydata",
          type: "fixed",
          label: "My entries",
          title: "My entries",
          icon: "house",
          align: "left",
          xPos: 1,
          settings: {
            columnSettings: {},
            columnWidths: {},
            filters: defaultLabNotebookEntriesFilters,
            sidebarFilters: {},
          },
          forcedSettings: {
            columnSettings: {},
            columnWidths: {},
            filters: { enteredByIds: session?.userId ? [session.userId] : null },
            sidebarFilters: {},
          },
        },
        trash: {
          tabId: "trash",
          type: "fixed",
          label: "Trash",
          title: "Trash",
          icon: "trash",
          align: "left",
          xPos: 3,
          settings: {
            columnSettings: {},
            columnWidths: {},
            filters: defaultLabNotebookEntriesFilters,
            sidebarFilters: {},
          },
          forcedSettings: {
            columnSettings: {},
            columnWidths: {},
            filters: { isSoftDeleted: true },
            sidebarFilters: {},
          },
        },
      }),
      [session?.userId]
    );
  return { defaults, tabStoreDefaults };
};

export const useLabNotebookEntriesTableColumns: UseEntityTableColumns<"notebookEntries"> = ({
  entityConstants,
  fieldLabels,
  sort,
  setSort,
}: UseEntityTableProps<"notebookEntries">) => {
  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<LabNotebookEntry> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => (
          <span style={{ color: "var(--gray-400)" }}>
            <samp>{row.id}</samp>
          </span>
        ),
        width: 150,
        align: "right",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        id: "default-entry-date",
        Header: fieldLabels.entryDate,
        accessor: (row) => (
          <div
            style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
          >
            <div>
              <LucideIcon name={entityConstants.icon} color={"var(--primary)"} />
            </div>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <DateTimeRenderer date={row.entryDate} includeElapsed={false} includeDate includeTime={false} />
            </div>
          </div>
        ),
        width: 170,
        minWidth: 120,
        align: "left",
        ...getAsyncSortingFn("ENTRY_DATE", sort, setSort),
      },
      {
        id: "default-name",
        Header: fieldLabels.name,
        accessor: (row) =>
          !!row.name ? (
            <div
              style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
            >
              <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                  <LucideIcon name={entityConstants.icon} color={"var(--primary)"} /> {row.name}
                </span>
              </div>
            </div>
          ) : (
            NotSet
          ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        id: "default-lab-notebook",
        Header: fieldLabels.labNotebook,
        accessor: (row) => (
          <div style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden" }}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                <LucideIcon name="book" color="var(--primary)" /> {row.labNotebook.name}
              </span>
            </div>
          </div>
        ),
        width: 300,
        // maxWidth: 150,
        align: "left",
        ...getAsyncSortingFn("LAB_NOTEBOOK_NAME", sort, setSort),
      },
      {
        id: "default-lab-notebook-experiment",
        Header: fieldLabels.labNotebookExperiment,
        accessor: (row) => (
          <div style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden" }}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                <LucideIcon name="file-text" color={"var(--primary)"} /> {row.labNotebookExperiment.name}
              </span>
            </div>
          </div>
        ),
        width: 300,
        // maxWidth: 150,
        align: "left",
        ...getAsyncSortingFn("LAB_NOTEBOOK_EXPERIMENT_NAME", sort, setSort),
      },
      {
        id: "default-enteredOn",
        Header: fieldLabels.enteredOn,
        accessor: (row) => (
          <div>
            <DateTimeRenderer date={row.enteredOn} includeElapsed={false} />
          </div>
        ),
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_ON", sort, setSort),
      },
      {
        id: "default-enteredBy",
        Header: fieldLabels.enteredBy,
        accessor: (row) => (
          <div>
            <GetPersons persons={[row.enteredBy]} createLinks={false} />
          </div>
        ),
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_BY", sort, setSort),
      },
      {
        id: "default-modifiedOn",
        Header: fieldLabels.modifiedOn,
        accessor: (row) => (
          <div>
            <DateTimeRenderer date={row.modifiedOn} includeElapsed={false} />
          </div>
        ),
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_ON", sort, setSort),
      },
      {
        id: "default-modifiedBy",
        Header: fieldLabels.modifiedBy,
        accessor: (row) => (
          <div>
            <GetPersons persons={[row.modifiedBy]} createLinks={false} />
          </div>
        ),
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_BY", sort, setSort),
      },
    ];

    return headers;
  }, [
    entityConstants.icon,
    fieldLabels.enteredBy,
    fieldLabels.enteredOn,
    fieldLabels.entryDate,
    fieldLabels.id,
    fieldLabels.labNotebook,
    fieldLabels.labNotebookExperiment,
    fieldLabels.modifiedBy,
    fieldLabels.modifiedOn,
    fieldLabels.name,
    setSort,
    sort,
  ]);

  return { columns };
};
export const LabNotebookEntriesTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  entityTypeId,
}: EntityTableProps<"notebookEntries">) => {
  const [showModal, setShowModal] = useState(false);
  const {
    bulkRestoreMutationAsync: bulkRestoreLabNotebookEntries,
    bulkDeleteMutationAsync: deleteLabNotebookEntries,
    isLoadingBulkRestoreMutation,
    isLoadingBulkDeleteMutation,
  } = entityApi;

  const { defaults, tabStoreDefaults } = useLabNotebookEntriesTableDefaults({ fieldLabels });

  const { selection, resultsCount, selectionPermissions, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<LabNotebookEntry>();

  const {
    filters,
    forcedFilters,
    sidebarFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    temporaryTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<LabNotebookEntry, LabNotebookEntryFilters, LabNotebookEntriesFilterForm>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
  });

  const { functionRef, sort, setSort, searchValue, setSearchValue, onTabChange, debouncedSearchValue } =
    useGenericVirtualizedTableTabs({
      currentTab,
      tabsLoading,
      filters,
      switchSortState: switchLabNotebookEntriesDefaultSortState,
      dispatchTabStore,
    });

  const { columns } = useLabNotebookEntriesTableColumns({ fieldLabels, entityConstants, sort, setSort });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
        </TableView.Head.Label>
        <TableView.Head.Controls>
          <LinkButton
            linkTo={routes.getAddRoute}
            className="btn btn-primary"
            title={`Add ${entityConstants.entitySingular}`}
            disabled={!permissions.canCreate}
          >
            <LucideIcon name="plus" /> Add {entityConstants.entitySingular}
          </LinkButton>
        </TableView.Head.Controls>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Sidebar>
          <LabNotebookEntriesFilterBar
            initialValues={sidebarFilters}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            currentTab={currentTab}
          />
        </TableView.Body.Sidebar>
        <TableView.Body.Content>
          <EntityTableTabs
            currentTab={currentTab}
            onTabChange={onTabChange}
            temporaryTabs={temporaryTabs}
            fixedTabs={fixedTabs}
            filters={{
              ...filters,
              isSoftDeleted: filters.isSoftDeleted || false,
              includeSoftDeleted: filters.includeSoftDeleted || false,
            }}
            sidebarFilters={sidebarFilters}
            columnSetting={columnSetting}
            columnWidths={columnWidths}
            customTabs={customTabs}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            tabsModified={tabsModified}
          />
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<LabNotebookEntry, LabNotebookEntryFilters>
                filters={filters}
                excludeFilters={{ includeSoftDeleted: (value) => !!value, isSoftDeleted: (value) => !!value }}
              />
              <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />
              <EntityTableEditButton
                entityConstants={entityConstants}
                routes={routes}
                permissions={permissions}
                selection={selection}
                selectionPermissions={selectionPermissions}
              />

              <MoreDropdown drop="right" btn="btn btn-ghost-secondary">
                <NestedDropdown label="Share / Export" icon="share-2" disabled={selection.size === 0}>
                  <li>
                    <ExportCsvButton
                      entityConstants={entityConstants}
                      columnSettings={columnSetting}
                      filters={{
                        ids: selection.size > 0 ? Array.from(selection) : undefined,
                        ...memoizedFilters,
                        searchTerm: debouncedSearchValue,
                      }}
                      disabled={selection.size === 0}
                    />
                  </li>
                  <li>
                    <ExportPdfButton
                      entityConstants={entityConstants}
                      filters={{
                        ids: selection.size > 0 ? Array.from(selection) : undefined,
                        ...memoizedFilters,
                        searchTerm: debouncedSearchValue,
                      }}
                      disabled={selection.size === 0}
                    />
                  </li>
                </NestedDropdown>
                {/* Locking & Signing */}
                <GenericEntityTableComplianceDropdown
                  entityTypeId={entityTypeId}
                  selection={selection}
                  selectionPermissions={selectionPermissions}
                  tableFunctionRef={functionRef}
                  currentTab={currentTab}
                  showSignButton
                />
                <li className="divider" />
                {/* Trash */}
                {currentTab === "trash" && (
                  <li>
                    <EntityTableRestoreButton
                      entityConstants={entityConstants}
                      selection={selection}
                      permissions={permissions}
                      selectionPermissions={selectionPermissions}
                      onClick={async () =>
                        await bulkRestoreLabNotebookEntries({ ids: Array.from(selection) }).catch(() => {})
                      }
                      loading={isLoadingBulkRestoreMutation}
                    />
                  </li>
                )}
                <li>
                  <EntityTableSoftDeletableButton
                    currentTab={currentTab}
                    entityConstants={entityConstants}
                    selection={selection}
                    permissions={permissions}
                    selectionPermissions={selectionPermissions}
                    onClick={() => setShowModal(true)}
                  />
                </li>
              </MoreDropdown>
              <AlertModal
                type={`${currentTab === "trash" ? "danger" : "warning"}`}
                showModal={showModal}
                setShowModal={setShowModal}
                forceUserInput={currentTab === "trash"}
                title={`${
                  selection.size === 0
                    ? `Select dataset to ${currentTab === "trash" ? "delete" : "trash"}`
                    : selection.size === 1
                    ? `${currentTab === "trash" ? "Delete" : "Trash"} the selected ${entityConstants.entitySingular}?`
                    : `${currentTab === "trash" ? "Delete" : "Trash"} the selected ${entityConstants.entityPlural}?`
                }`}
                description={`${
                  currentTab === "trash"
                    ? `Proceeding will permanently delete the selected ${entityConstants.entityPlural}.`
                    : `Proceeding will move the selected ${entityConstants.entityPlural} into trash.`
                }`}
                proceedLabel={`${currentTab === "trash" ? "Delete" : "Trash"}`}
                onProceed={async () => {
                  await deleteLabNotebookEntries({
                    ids: Array.from(selection),
                    goBackOnSuccess: false,
                    showToast: false,
                    entityName: entityConstants.resource,
                    params: { deletePermanently: currentTab === "trash" },
                  }).catch((e) => {});
                  functionRef.current?.resetSelection();
                  setShowModal(false);
                }}
                loading={isLoadingBulkDeleteMutation}
              />
            </EntityTable.Controls>
            <EntityTable.Body<LabNotebookEntry, LabNotebookEntryFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={defaults}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn
              loading={tabsLoading}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
